// LeafletGeoMap.tsx - Professional IP Geolocation map using Leaflet
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, CircleMarker, Popup, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { geolocationAPI } from '../../services/api';
import api from '../../services/api';
import ThemeContext from '../../contexts/ThemeContext';

// Types
interface MapFeature {
  type: string;
  properties: {
    ip: string;
    hostname?: string;
    country: string;
    country_code?: string;
    city: string;
    isp: string;
    open_ports?: number[];
    services?: string[];
    vuln_count?: number;
    severity?: string;
    scan_id?: number;
    target_id?: number;
    lat?: number;
    lon?: number;
  };
  geometry: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
}

interface MapData {
  type: string;
  features: MapFeature[];
}

interface LeafletGeoMapProps {
  scanId?: number;
  projectId?: number;
  reconScanId?: number; // For recon scan map data
  showAllUserTargets?: boolean; // New prop: show all targets user can access
  title?: string;
  showTable?: boolean;
  height?: string;
  showRefreshButton?: boolean; // Show refresh button
}

// Severity colors
const severityColors: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#2563eb',
  info: '#6b7280',
};

// Country flag emoji helper
const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Component to fit bounds to all markers
const FitBounds: React.FC<{ features: MapFeature[] }> = ({ features }) => {
  const map = useMap();

  useEffect(() => {
    if (features.length > 0) {
      const validFeatures = features.filter(f =>
        f.geometry.coordinates[0] !== 0 || f.geometry.coordinates[1] !== 0
      );

      if (validFeatures.length > 0) {
        const bounds = validFeatures.map(f => [
          f.geometry.coordinates[1], // lat
          f.geometry.coordinates[0], // lon
        ] as [number, number]);

        if (bounds.length === 1) {
          map.setView(bounds[0], 6);
        } else {
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
        }
      }
    }
  }, [features, map]);

  return null;
};

// Map tile URLs for different themes
const mapTiles = {
  dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
};

const LeafletGeoMap: React.FC<LeafletGeoMapProps> = ({
  scanId,
  projectId,
  reconScanId,
  showAllUserTargets = false,
  title,
  showTable = true,
  height = '400px',
  showRefreshButton = true,
}) => {
  const { t } = useTranslation();
  const displayTitle = title || t('projectOverview.targetLocations');
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheStats, setCacheStats] = useState<{ db_cached_ips?: number } | null>(null);

  // Get theme from context
  const themeContext = useContext(ThemeContext);
  const isDarkTheme = themeContext?.theme === 'dark' ||
    (themeContext?.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const loadMapData = async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (showAllUserTargets) {
        // Fetch all targets the user can see across all projects
        response = await api.get('/geolocation/all-targets/map');
      } else if (reconScanId) {
        // Fetch recon scan map data
        response = await geolocationAPI.getReconScanMap(reconScanId);
      } else if (scanId) {
        response = await geolocationAPI.getScanMap(scanId);
      } else if (projectId) {
        response = await geolocationAPI.getProjectMap(projectId);
      } else {
        throw new Error('Either scanId, projectId, reconScanId, or showAllUserTargets is required');
      }

      // Filter out 0.0.0.0 IPs and entries without valid coordinates
      const filteredFeatures = (response.data.map?.features || []).filter((f: MapFeature) => {
        const ip = f.properties.ip;
        // Skip 0.0.0.0 and internal/private IPs
        if (ip === '0.0.0.0' || ip.startsWith('127.') || ip.startsWith('10.') ||
            ip.startsWith('192.168.') || ip.startsWith('172.16.') || ip.startsWith('172.17.') ||
            ip.startsWith('172.18.') || ip.startsWith('172.19.') || ip.startsWith('172.20.') ||
            ip.startsWith('172.21.') || ip.startsWith('172.22.') || ip.startsWith('172.23.') ||
            ip.startsWith('172.24.') || ip.startsWith('172.25.') || ip.startsWith('172.26.') ||
            ip.startsWith('172.27.') || ip.startsWith('172.28.') || ip.startsWith('172.29.') ||
            ip.startsWith('172.30.') || ip.startsWith('172.31.')) {
          return false;
        }
        // Skip entries without valid coordinates
        if (!f.geometry?.coordinates ||
            (f.geometry.coordinates[0] === 0 && f.geometry.coordinates[1] === 0)) {
          return false;
        }
        return true;
      });

      setMapData({
        type: 'FeatureCollection',
        features: filteredFeatures,
      });

      // Also fetch cache stats
      try {
        const statsResponse = await api.get('/geolocation/stats');
        setCacheStats(statsResponse.data);
      } catch (e) {
        // Ignore stats errors
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load geolocation data');
      console.error('Geolocation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Clear memory cache first (faster), then reload
      await api.post('/geolocation/cache/clear');
      await loadMapData();
    } catch (err: any) {
      setError(err.message || 'Failed to refresh geolocation data');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMapData();
  }, [scanId, projectId, reconScanId, showAllUserTargets]);

  // Group features by country
  const countrySummary = useMemo(() => {
    if (!mapData?.features) return [];

    const countryMap = new Map<string, {
      count: number;
      vulns: number;
      countryCode: string;
      features: MapFeature[]
    }>();

    mapData.features.forEach(feature => {
      const country = feature.properties.country || 'Unknown';
      const countryCode = feature.properties.country_code || '';
      const existing = countryMap.get(country) || { count: 0, vulns: 0, countryCode, features: [] };
      existing.count++;
      existing.vulns += feature.properties.vuln_count || 0;
      existing.features.push(feature);
      countryMap.set(country, existing);
    });

    return Array.from(countryMap.entries())
      .map(([country, data]) => ({ country, ...data }))
      .filter(c => c.country !== 'Unknown') // Filter out Unknown
      .sort((a, b) => b.count - a.count);
  }, [mapData]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height,
        backgroundColor: 'var(--color-surface-elevated)',
        borderRadius: '8px',
        color: 'var(--color-text-tertiary)',
      }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
        {t('projectOverview.loadingMapData')}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height,
        backgroundColor: 'var(--color-tint-red)',
        borderRadius: '8px',
        color: 'var(--color-critical)',
      }}>
        <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
        {error}
      </div>
    );
  }

  if (!mapData?.features || mapData.features.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height,
        backgroundColor: 'var(--color-surface-tertiary)',
        borderRadius: '8px',
        color: 'var(--color-text-muted)',
        gap: '0.5rem',
      }}>
        <i className="fas fa-globe" style={{ fontSize: '2rem' }}></i>
        <span>{t('projectOverview.noGeolocationData')}</span>
        <span style={{ fontSize: '0.75rem' }}>{t('projectOverview.externalIPsWillBeGeolocated')}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Map Container */}
      <div style={{
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid var(--color-border-primary)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem 1rem',
          backgroundColor: isDarkTheme ? 'var(--color-surface-tertiary)' : 'var(--color-primary-500)',
          color: 'var(--color-text-inverse)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-globe-americas"></i>
            {displayTitle && <span style={{ fontWeight: 500 }}>{displayTitle}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {cacheStats?.db_cached_ips !== undefined && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                <i className="fas fa-database" style={{ marginRight: '0.25rem' }}></i>
                {cacheStats.db_cached_ips} {t('projectOverview.cached')}
              </span>
            )}
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
              {mapData.features.length} {t('projectOverview.locations')}
            </span>
            {showRefreshButton && (
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                style={{
                  background: 'var(--color-card-shadow)',
                  border: '1px solid var(--color-card-shadow)',
                  borderRadius: '4px',
                  padding: '0.25rem 0.5rem',
                  color: 'var(--color-text-inverse)',
                  cursor: refreshing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                }}
                title={t('projectOverview.refreshGeolocationData')}
              >
                <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                {t('common.refresh', 'Refresh')}
              </button>
            )}
          </div>
        </div>

        <MapContainer
          center={[30, 0]}
          zoom={2}
          style={{ height, width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url={isDarkTheme ? mapTiles.dark : mapTiles.light}
          />
          <ZoomControl position="topright" />
          <FitBounds features={mapData.features} />

          {mapData.features.map((feature, index) => {
            const [lon, lat] = feature.geometry.coordinates;
            const severity = feature.properties.severity || 'info';
            const color = severityColors[severity] || '#6b7280';
            const vulnCount = feature.properties.vuln_count || 0;
            const radius = Math.min(12, 6 + vulnCount * 0.3);

            return (
              <CircleMarker
                key={`marker-${index}`}
                center={[lat, lon]}
                radius={radius}
                pathOptions={{
                  color: 'white',
                  weight: 2,
                  fillColor: color,
                  fillOpacity: 0.8,
                }}
              >
                <Popup>
                  <div style={{ minWidth: '200px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', borderBottom: '1px solid var(--color-border-primary)', paddingBottom: '0.5rem' }}>
                      {feature.properties.ip}
                      {feature.properties.hostname && feature.properties.hostname !== feature.properties.ip && (
                        <div style={{ fontWeight: 'normal', fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>
                          {feature.properties.hostname}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem' }}>
                      <div><strong>{t('projectOverview.location')}:</strong> {feature.properties.city}, {feature.properties.country}</div>
                      {feature.properties.isp && <div><strong>{t('projectOverview.isp')}:</strong> {feature.properties.isp}</div>}
                      <div>
                        <strong>{t('projectOverview.findings')}:</strong>{' '}
                        <span style={{
                          color: severityColors[severity],
                          fontWeight: 'bold',
                        }}>
                          {vulnCount} ({severity})
                        </span>
                      </div>
                      {feature.properties.open_ports && feature.properties.open_ports.length > 0 && (
                        <div><strong>{t('projectOverview.ports')}:</strong> {feature.properties.open_ports.slice(0, 5).join(', ')}{feature.properties.open_ports.length > 5 ? '...' : ''}</div>
                      )}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Legend */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: isDarkTheme ? 'var(--color-surface-tertiary)' : 'var(--color-primary-500)',
          fontSize: '0.75rem',
          color: 'var(--color-border-primary)',
          flexWrap: 'wrap',
        }}>
          {Object.entries(severityColors).map(([sev, color]) => (
            <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
              }}></span>
              {t(`severity.${sev}`)}
            </div>
          ))}
        </div>
      </div>

      {/* Country Summary Table */}
      {showTable && countrySummary.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <i className="fas fa-flag" style={{ color: 'var(--color-primary-500)' }}></i>
            {t('projectOverview.targetsByCountry')}
          </h3>
          <div style={{
            backgroundColor: 'var(--color-card-background)',
            borderRadius: '8px',
            border: '1px solid var(--color-border-primary)',
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-surface-elevated)' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>{t('projectOverview.country')}</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem' }}>{t('projectOverview.targets')}</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem' }}>{t('projectOverview.findings')}</th>
                </tr>
              </thead>
              <tbody>
                {countrySummary.map((item, index) => (
                  <tr key={index} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ marginRight: '0.5rem' }}>{getCountryFlag(item.countryCode)}</span>
                      {item.country}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>{item.count}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: item.vulns > 0 ? 'var(--color-critical)' : 'var(--color-text-tertiary)' }}>
                      {item.vulns}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeafletGeoMap;
